import { Component, OnInit } from '@angular/core';
import { AccountUnitOfWork } from '@data';
import { ActiveStatusEnum } from '@models';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import {
  IAvailableStatusDto,
  IStatusEnum,
  IStatusResult,
  ISubjectStatusIdentifier,
  ProductRelatedEntity,
  StatusService,
} from '../services/status.service';
import { UtilDialogService } from '../services/dialog.service';
import { firstValueFrom } from 'rxjs';

export interface IAvailableStatusDialogParams {
  entity: ProductRelatedEntity;
  subjectTypeName: string;
}

@Component({
  selector: 'prox-available-status-dialog',
  standalone: false,
  templateUrl: './available-status-dialog.component.html',
})
export class AvailableStatusDialogComponent implements OnInit {
  params!: IAvailableStatusDialogParams;
  identifiers: ISubjectStatusIdentifier[] = [];
  availableStatuses: IAvailableStatusDto[] = [];
  reportStatusResult?: IStatusResult;
  isAvailable?: boolean;
  entityType?: string;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<IAvailableStatusDialogParams>,
    public statusService: StatusService,
    public dialogService: UtilDialogService,
    private accountUow: AccountUnitOfWork,
  ) {}

  public static async open(
    dialogService: DialogService,
    data: IAvailableStatusDialogParams,
    config: DynamicDialogConfig<IAvailableStatusDialogParams>,
  ) {
    const ref = dialogService.open(AvailableStatusDialogComponent, {
      closable: true,
      draggable: true,
      width: '600px',
      height: 'auto',
      // baseZIndex: 10000,
      focusOnShow: true,
      modal: true,
      ...config,
      data: data,
    });
    const r = await firstValueFrom(ref.onClose);
    return r;
  }

  ngOnInit() {
    this.params = this.config.data!;
    this.availableStatuses = this.statusService.createStatusReporterDtos(this.params.entity);
    this.identifiers = this.statusService.getSubjectIdentifiers(this.params.entity);
    this.reportStatusResult = this.statusService.getReportStatusResult(this.params.entity);
    this.entityType = this.params?.subjectTypeName;

    this.isAvailable =
      this.reportStatusResult?.maxStatusId === IStatusEnum.Active || this.reportStatusResult?.baseStatusId === IStatusEnum.Phaseout;
  }

  getSeverity(activeStatus: ActiveStatusEnum) {
    switch (activeStatus) {
      case ActiveStatusEnum.Active:
        return 'success';
      case ActiveStatusEnum.Phaseout:
        return 'warn';
      default:
        return 'danger';
    }
  }

  /** Phaseout gets its own dark yellow tag; 'warn' alone is orange and reads like the red Inactive. */
  getTagClass(activeStatus: ActiveStatusEnum) {
    return activeStatus === ActiveStatusEnum.Phaseout ? 'qb-tag-phaseout' : '';
  }

  onOk() {
    this.ref.close();
  }

  async onShowInsights() {
    // Open help dialog with anchor to availability-status section
    // Using a mock parent object with the selector for the price-lists component
    const parent = { selector: 'prox-price-lists' };
    await this.dialogService.helpDialog(
      'Product Availability',
      parent,
      undefined,
      undefined,
      this.accountUow,
      'availability-status',
    );
  }
}
